<template>
    <div class="form-wrapper">
        <div class="timer-block">
            <div class="timer-container">
                <div id="countdown">
                    <div class="countdown-inner">
                        <div id="countdown-number" class="no-reload">{{ formattedCountdown }}</div>
                        <div v-if="codeExpired" class="code-expires-label">{{ $t("code expires") }}</div>
                    </div>
                    <svg class="timer">
                        <circle class="trace" r="57" cx="50%" cy="50%"></circle>
                        <circle r="57" cx="50%" cy="50%" :style="circleStyle" :class="codeExpired ? 'trace' : ''"></circle>
                    </svg>
                </div>
            </div>
        </div>

        <div class="TwoFA-form">
            <div class="login-btns-wrap" v-if="codeExpired">
                <button @click="resendCodeSubmit"
                    class="mt-30 button button-accent button-submit btn-hover btn-hover--green">
                    <span>
                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.5607 4.9375C18.635 4.00606 17.5339 3.26695 16.321 2.76279C15.1081 2.25863 13.8074 1.99938 12.4937 2C6.96529 2 2.5 6.475 2.5 12C2.5 17.525 6.96529 22 12.4937 22C17.1592 22 21.0491 18.8125 22.1623 14.5H19.5607C19.0454 15.9619 18.0889 17.228 16.8231 18.1235C15.5573 19.0191 14.0446 19.5001 12.4937 19.5C8.35366 19.5 4.98906 16.1375 4.98906 12C4.98906 7.8625 8.35366 4.5 12.4937 4.5C14.57 4.5 16.4212 5.3625 17.772 6.725L13.7445 10.75H22.5V2L19.5607 4.9375Z" fill="#F6F6F6"/>
                        </svg>
                        {{ $t("Resend Code") }}
                    </span>
                </button>
            </div>
            <form @submit.prevent="submitFormEmail" novalidate v-else>
                <div class="form-group fake-code-container" :class="codeError ? 'has-error' : ''">
                    <div class="code-container">
                        <input v-for="(input, index) in code" :key="index" v-model="code[index]"
                            @input="handleInput(index, $event)" @keydown="handleKeydown(index, $event)"
                            @paste="handlePaste" ref="codeField" :type="isMobile ? 'number' : 'text'" max-length='1'
                            class="form-control code-field" aria-required="true" placeholder="0" />
                    </div>
                    <div class="help-block" v-if="codeError">{{ codeError }}</div>
                </div>
                <div class="login-btns-wrap">
                    <Button type="submit" :buttonText="$t('Send Request')" :isFormChanged="isFormChangedEmail" colorClass="green"
                        :showPreloader="showPreloader" additionalClasses="button-accent button-submit" />
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import Button from '/webpack-assets/vue/components/Button.vue';
import { renderAlertVue } from "/webpack-assets/vue/utils/common.js";

export default {
    name: 'Reset',
    emits: ['new-password', 'update-form-loader', 'auth-success', 'change-height-block'],
    components: {
        Button
    },
    props: {
        twoFAType: {
            type: String,
            default: ''
        },
        twoFAttl: {
            type: Number,
            default: 120
        },
        stepProgress: {
            type: Object,
        },
        authData: {
            type: Object,
        }
    },
    data() {
        return {
            code: ['', '', '', '', '', ''],
            codeError: '',
            isProcessingError: false,
            isFormChangedEmail: false,
            countdown: 120,
            totalCountdownTime: 120,
            interval: null,
            showPreloader: false,
            codeExpired: false,
            isLogin: this.twoFAType === 'login',
            isSignup: this.twoFAType === 'signup',
            isEmailRequest: this.twoFAType === 'resetLogin',
            isUsernameRequest: this.twoFAType === 'resetUsername',
            settings: window.settings,
        };
    },
    computed: {
        formattedCountdown() {
            const minutes = Math.floor(this.countdown / 60);
            const seconds = this.countdown % 60;
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        },
        circleStyle() {
            const progressOffset = -375;
            const progressStep = -progressOffset / this.totalCountdownTime;
            const currentOffset = progressOffset + (progressStep * (this.totalCountdownTime - this.countdown));
            return {
                strokeDashoffset: currentOffset,
            };
        },
        isMobile() {
            return window.innerWidth <= 768;
        },
        dots() {
            return new Array(this.stepProgress.all).fill(null);
        },
        isDifmark() {
            return this.authData?.company_name === 'Difmark' ? true : false;
        }
    },
    methods: {
        handleInput(index, event) {
            const value = event.target.value.replace(/[^\d]/g, '');
            if (this.isProcessingError) return;
            if (value) {
                this.codeError = '';
                this.$emit('change-height-block', 0)
                this.code.splice(index, 1, value.slice(0, 1));
                if (index < this.code.length - 1) {
                    this.$refs.codeField[index + 1].focus();
                }
            } else {
                this.code.splice(index, 1, '');
            }

            this.updateTrueInput();
            if (this.code.join('').length === 6) {
                this.submitFormEmail();
                this.showPreloader = true;
            }
        },
        handleKeydown(index, event) {
            if (this.isProcessingError) {
                this.isProcessingError = false;
                return;
            }
            if (event.key !== 'Tab' && event.key !== 'Shift') {
                this.codeError = '';
                this.$emit('change-height-block', 0)
            }
            if (event.key === 'Backspace' && !this.code[index]) {
                if (index > 0) {
                    this.$refs.codeField[index - 1].focus();
                }
            }
        },
        handlePaste(event) {
            const pastedValue = event.clipboardData.getData('Text').replace(/\D/g, '');
            this.codeError = '';
            this.$emit('change-height-block', 0)
            if (pastedValue.length === 6) {
                pastedValue.split('').forEach((digit, index) => {
                    if (index < this.code.length) {
                        this.code[index] = digit;
                    }
                });

                this.$nextTick(() => {
                    this.$refs.codeField[5].focus();
                });

                this.updateTrueInput();
                this.submitFormEmail();
                this.showPreloader = true;
            }
        },
        resendCodeSubmit() {
            this.code = ['', '', '', '', '', ''];
            this.codeError = '';
            this.$emit('change-height-block', 0)
            const form = new FormData();
            form.append('_csrf-frontend', this.authData.csrf);
            axios({
                method: "POST",
                url: '/' + this.authData?.language + '/auth-api/mfa-recreate',
                data: form,
                headers: { "Content-Type": "multipart/form-data" },
            })
                .then(response => {
                    const resp = response.data
                    if (resp.status === 'success') {
                        this.codeExpired = false;
                        this.countdown = resp.ttl;
                        this.startCountdown();
                    }
                })
                .catch(error => {
                    console.error(error)
                })
        },
        handleCodeError(resp) {
            this.isProcessingError = true;
            this.codeError = resp.errors?.code ? resp.errors.code[0] : this.$t('Code is invalid.');
            this.$emit('change-height-block', 17);
            this.formLoader = false;
            this.code = ['', '', '', '', '', ''];
            this.$nextTick(() => {
                this.$refs.codeField.forEach(field => field.blur());
                this.isFormChangedEmail = false;
                this.showPreloader = false;
            });
        },
        updateTrueInput() {
            const otpValue = this.code.join('');
            this.isFormChangedEmail = otpValue.length === 6;
        },
        submitFormEmail() {
            if (this.isFormChangedEmail && !this.formLoader) {
                this.formLoader = true;
                let url;

                if (this.twoFAType === 'resetLogin' || this.twoFAType === 'resetUsername') {
                    url = '/auth-api/confirm-reset-password';
                } else if (this.twoFAType === 'login') {
                    url = '/auth-api/mfa-auth';
                } else if (this.twoFAType === 'signup') {
                    url = '/auth-api/mfa-signup';
                }

                let otpValue = this.code.join('');
                const form = new FormData();
                form.append('MfaEmailForm[code]', otpValue);
                form.append('_csrf-frontend', this.authData.csrf);
                axios({
                    method: "POST",
                    url: '/' + this.authData?.language + url,
                    data: form,
                    headers: { "Content-Type": "multipart/form-data" },
                })
                    .then(response => {
                        const resp = response.data;
                        if (resp.status === 'error') {
                            this.handleCodeError(resp);
                        } else if (resp.status === 'success') {
                            if (this.twoFAType === 'resetLogin' || this.twoFAType === 'resetUsername') {
                                this.$emit('new-password', { token: resp.token });
                            } else if (this.twoFAType === 'signup') {
                                renderAlertVue(resp.message, 'success', 3000);
                                this.$emit('update-form-loader', this.formLoader);
                                this.$emit('auth-success', resp)
                            } else {
                                this.$emit('update-form-loader', this.formLoader);
                                this.$emit('auth-success', resp)
                            }
                        }
                    })
                    .catch(error => {
                        const resp = error.response?.data;
                        if (resp) {
                            this.handleCodeError(resp);
                        } else {
                            this.codeError = this.$t('Code is invalid.');
                            this.formLoader = false;
                            this.showPreloader = false;
                        }
                    });
            }
        },
        startCountdown() {
            this.interval = setInterval(() => {
                if (this.countdown > 0) {
                    this.countdown--;
                } else {
                    clearInterval(this.interval);
                    this.codeExpired = true;
                    this.$emit('change-height-block', -80);
                }
            }, 1000);
        },
    },
    mounted() {
        this.countdown = this.twoFAttl;
        this.$nextTick(() => {
            this.$refs.codeField[0].focus();
            this.startCountdown();
        });
    },
    beforeDestroy() {
        clearInterval(this.interval);
    }
};
</script>